import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  OnDestroy,
  untracked,
  ViewEncapsulation,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import {
  ActiveConversationStore,
  ActivityService,
  FooterButtonType,
  MessageButtonType,
  MessageSenderType,
  MessageService,
  SidebarButtonType,
  ConversationHelperService,
  MessageStore,
  AssetsPreviewDialogComponent,
  ConversationHeaderComponent,
  FindByPipe,
  ParticipantListComponent,
  logger,
  ErrorStore,
  ConfirmDialogComponent,
  MessageButtonClickEvent,
  WindowService,
  ParticipantWithSourceId,
  UserState,
  ConversationTypeStore,
  AppTypeService,
} from '../../../index';
import { Message, ConversationType, Location } from '@clientwire/api';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FilesToUpload } from '@akostm/orbit-ui';
import * as uuid from 'uuid';
import { ConversationItemSidebarComponent } from '../conversation-item-sidebar/conversation-item-sidebar.component';
import { ConversationComponent } from '../conversation/conversation.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastrService } from 'ngx-toastr';
import { EditParticipantDialogComponent } from '../dialogs/edit-participant-dialog/edit-participant-dialog.component';

@Component({
  selector: 'shared-conversation-item',
  standalone: true,
  imports: [
    CommonModule,
    ConversationComponent,
    ConversationHeaderComponent,
    ConversationItemSidebarComponent,
    FindByPipe,
    ParticipantListComponent,
  ],
  templateUrl: './conversation-item.component.html',
  styleUrl: './conversation-item.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ConversationItemComponent implements AfterViewInit, OnDestroy {
  //#region Inputs
  readonly conversationId = input.required<string>();
  readonly conversationTypeId = input.required<string>();
  readonly user = input<UserState>();
  readonly isActive = input<boolean | undefined>(false);
  readonly isCollapsed = input<boolean>(false);
  readonly conversationTypes = input<ConversationType[]>();
  readonly isDraggable = input<boolean>(false);
  readonly isCloseable = input<boolean>(false);
  //#endregion

  //#region Outputs
  readonly messageRead = output<Message>();
  readonly conversationItemInited = output<string>();
  //#endregion

  //#region Services
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #activityService = inject(ActivityService);
  readonly #messageService = inject(MessageService);
  readonly #translateService = inject(TranslateService);
  readonly #library = inject(FaIconLibrary);
  readonly #dialog = inject(Dialog);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #messageStore = inject(MessageStore);
  readonly #errorStore = inject(ErrorStore);
  readonly #clipboard = inject(Clipboard);
  readonly #toastr = inject(ToastrService);
  readonly windowService = inject(WindowService);
  readonly #conversationTypeStore = inject(ConversationTypeStore);
  readonly #appTypeService = inject(AppTypeService);
  //#endregion

  //#region Computed
  readonly isInFocus = computed(
    () => this.#activeConversationStore.inFocusConversationId() === this.conversationId()
  );
  readonly conversation = computed(
    () => this.#activeConversationStore.entityMap()[this.conversationId()]
  );
  readonly #messageEntity = computed(() => this.#messageStore.entityMap()[this.conversationId()]);
  readonly messages = computed(() => this.#messageEntity()?.messages ?? []);
  readonly #isAtBottom = computed(() => this.conversation()?.scrolledToBottom ?? false);
  readonly #lastReadSequence = computed(
    () => this.conversation()?.readStatus?.lastMessageSeen ?? 0
  );
  readonly otherTypingParticipants = computed(() => this.#calculateOtherTypingParticipants());
  readonly messagePrependLoading = computed(
    () => this.#messageEntity()?.messagePrependLoading ?? false
  );
  readonly messageAppendLoading = computed(
    () => this.#messageEntity()?.messageAppendLoading ?? false
  );
  readonly showJoinIcon = computed(
    () => !this.conversation()?.participants?.some((p) => p.userId === this.user()?.id)
  );
  readonly messageIdsWithError = computed(() => this.#messageEntity()?.messageIdsWithError);
  readonly errors = computed(() =>
    this.#errorStore.getErrorsByLocations(this.#messageErrorLocations())
  );
  readonly #messageErrorLocations = computed(() => [
    `${this.conversationId()}:getMessagesOfConversation`,
  ]);

  readonly isConversationLocked = computed(() => {
    const conversation = this.conversation();
    if (conversation?.lockExpiresAt) {
      return new Date() < conversation.lockExpiresAt;
    }
    return false;
  });

  readonly conversationLockedNote = computed(() => {
    const conversation = this.conversation();
    if (conversation?.lockNote) {
      return conversation.lockNote;
    }
    return 'SHARED.CONVERSATION_LOCKED.DEFAULT';
  });

  readonly userIsNotParticipant = computed(() => {
    return !this.conversation()?.participants?.find(
      (participant) => participant.userId === this.user()?.id
    );
  });

  readonly templates = computed(() => {
    const conversationType = this.conversationTypes()?.find(
      (conversationType) => conversationType.id == this.conversationTypeId()
    );
    if (!conversationType?.templates) return undefined;
    const templateMap = new Map(Object.entries(conversationType.templates));
    return templateMap;
  });
  //#endregion

  //#region View Children
  readonly moreButton = viewChild('reference', { read: ElementRef });
  readonly conversationComponent = viewChild(ConversationComponent);
  //#endregion

  //#region Variables
  readonly showParticipantList = signal<boolean>(true);
  readonly #now = signal<Date>(new Date());
  readonly sidebarButtonTypeEnum = SidebarButtonType;
  readonly senderType = MessageSenderType.TenantUser;
  readonly sharedUserDisplayName = computed(
    () =>
      this.#conversationTypeStore.conversationTypeById(this.conversationTypeId())
        ?.displayNameSharedByUsers
  );
  readonly conversationAppType = computed(() => {
    const appType = this.#appTypeService.type();
    if (appType === 'main' || appType === 'embed') {
      return appType;
    }
    return 'client';
  });
  #setToFocus = false;
  #dateUpdaterTimout: number | null = null;
  #messagesInited = false;
  #messagesLength = 0;
  //#endregion

  constructor() {
    this.#library.addIconPacks(fas);

    effect(() => {
      if (this.isInFocus()) {
        if (!this.#setToFocus) {
          this.#setToFocus = true;
          if (untracked(this.#isAtBottom)) {
            this.#messageService.setLastUnreadMessage(this.conversationId());
          }
        }
      } else {
        this.#setToFocus = false;
      }
    });

    // This effect is triggered when the conversation is open and the messages are loaded
    // It check conditions and if all are met, it sets the unread message
    effect(async () => this.#sendMessageReadEventWhenNeeded());

    this.#updateDateSignalToCheck();
  }

  ngAfterViewInit(): void {
    const conversationId = this.conversationId();
    if (conversationId) {
      this.conversationItemInited.emit(conversationId);
    }
  }

  ngOnDestroy(): void {
    if (this.#dateUpdaterTimout !== null) {
      clearTimeout(this.#dateUpdaterTimout);
    }
  }

  toggleParticipantList() {
    this.showParticipantList.update((prev) => !prev);
  }

  handleSendMessage(event: FooterButtonType) {
    this.#messageService.sendMessage(event, this.conversationId());
  }

  handleMessageButtonClick(event: MessageButtonClickEvent) {
    switch (event.type) {
      case MessageButtonType.LocationRequest: {
        if (!this.userIsNotParticipant()) {
          this.#conversationHelperService.openLocationSelectorOverlay(
            this.conversationId(),
            this.conversation()?.messageSourceId
          );
        } else {
          this.#toastr.warning(
            this.#translateService.instant('SHARED.PARTICIPANT_LIST.ADD_YOURSELF')
          );
        }
        break;
      }

      case MessageButtonType.CopyCoordinates: {
        this.#copyCoordinates(event.data);
        break;
      }

      case MessageButtonType.OpenGoogleMaps: {
        this.#openGoogleMaps(event.data);
        break;
      }

      default: {
        logger.debug('unhandled event: ', event);
        break;
      }
    }
  }

  #copyCoordinates(coordinates: Location) {
    this.#clipboard.copy(`${coordinates.latitude},${coordinates.longitude}`);
    this.#toastr.success(this.#translateService.instant('SHARED.MESSAGES.COORDINATES_COPIED'));
  }

  #openGoogleMaps(coordinates: Location) {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`,
      '_blank'
    );
  }

  handleResendMessage(messageId: string) {
    this.#messageService.resendMessage(this.conversationId(), messageId);
  }

  handleAssetClick(messageId: string) {
    this.#openAssetDialog(messageId);
  }

  handleMessageLimitChange(element: HTMLElement, conversationId: string) {
    const limit = this.#messageStore.recalculateMessagesLimit(conversationId, element);
    if (limit > this.messages().length && this.messages().length !== 0) {
      // If the conversation item size changes but the messages are less then the limit,
      // we need to load more messages to have a scroll bar, if there are messages on the backend
      this.#loadMoreMessages('append', limit);
    }
  }

  handleScrolledToBottom(event: boolean) {
    this.#setActiveConversationScrollState(this.conversationId(), event);
  }

  handleFilesChange(files?: FilesToUpload[]) {
    if (files) {
      this.#activeConversationStore.updateConversation(this.conversationId(), { files });
    }
  }

  handleIsOpenEvent(stillOpen: boolean) {
    if (stillOpen) {
      this.#activityService.handleConversationOpenActivity(this.conversationId());
    }
  }

  #setActiveConversationScrollState(conversationId: string, scrolledToBottom: boolean) {
    if (this.#activeConversationStore.inFocusConversationId() === conversationId) {
      this.#activeConversationStore.updateConversation(conversationId, { scrolledToBottom });
    }
  }

  #openAssetDialog(messageId: string) {
    const message = this.messages().find((msg) => msg.id === messageId);
    this.#dialog.open(AssetsPreviewDialogComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-dialog-backdrop',
      data: {
        messages: this.messages(),
        selectedMessage: message,
      },
    });
  }

  async sendIsTypingEvent(sourceId: boolean) {
    if (sourceId) {
      if (!this.conversation()?.messageSourceId) {
        this.#activeConversationStore.setNewMessageSourceId(this.conversationId(), uuid.v4());
      }
      await this.#activityService.handleIsTypingActivity(
        this.conversationId(),
        'me',
        this.conversation()?.messageSourceId
      );
    }
  }

  // This function is triggering the computed signals to compute again
  #updateDateSignalToCheck() {
    this.#now.set(new Date());
    if (this.#dateUpdaterTimout !== null) {
      clearTimeout(this.#dateUpdaterTimout);
    }
    this.#dateUpdaterTimout = window.setTimeout(() => {
      this.#updateDateSignalToCheck();
    }, 1000);
  }

  fetchForNewMessages() {
    this.#loadMoreMessages('prepend', 10, true);
  }

  handleDownScroll(): void {
    this.#loadMoreMessages('prepend');
  }

  handleUpScroll(): void {
    this.#loadMoreMessages();
  }

  #loadMoreMessages(
    position: 'prepend' | 'append' = 'append',
    limit = 10,
    fetchUntilHasMoreMessages = false
  ): void {
    this.#conversationHelperService.loadMoreMessages(
      this.#messageEntity(),
      position,
      limit,
      fetchUntilHasMoreMessages
    );
  }

  refreshMessages() {
    this.#messageStore.getMessagesOfConversation(this.conversationId(), 10, undefined, 'append');
  }

  #calculateOtherTypingParticipants() {
    const conversation = this.conversation();
    const messages = this.messages();
    if (!conversation) return undefined;
    const otherParticipants = conversation.participants?.filter((p) => {
      const activeUserParticipantId = this.#activityService.findParticipantIdForConversation(
        conversation.id
      );
      return p.id !== activeUserParticipantId;
    });
    const lastMessage = messages[0];
    return otherParticipants?.filter((p) => {
      if (p.sourceId) {
        return (
          this.#now().getTime() - (p.wasTypingAt?.getTime() ?? 0) < 5000 &&
          lastMessage?.sourceId !== p.sourceId
        );
      }
      return this.#now().getTime() - (p.wasTypingAt?.getTime() ?? 0) < 5000;
    });
  }

  async #sendMessageReadEventWhenNeeded() {
    const inFocus = untracked(this.isInFocus);
    const atBottom = untracked(this.#isAtBottom);
    const lastReadSequence = untracked(this.#lastReadSequence);
    const messageEntity = this.#messageEntity();
    if (
      !messageEntity ||
      (messageEntity.lastUpdateEvent !== 'serverMessages' &&
        messageEntity.lastUpdateEvent !== 'socketMessage')
    )
      return;

    const msgs = messageEntity.messages;
    if (msgs.length !== this.#messagesLength) {
      this.#messagesLength = msgs.length;
    } else {
      return;
    }
    // If not in focus or not scrolled to bottom, do nothing
    if (!inFocus || !atBottom || !msgs?.length) return;

    const newestMsg = msgs[0];
    if (!newestMsg) return;
    const highestSequence = newestMsg?.sequence ?? 0;

    // 1) first time we arrive (conversation is open & scrolled bottom)
    if (!this.#messagesInited) {
      this.#messagesInited = true;
      if (highestSequence > lastReadSequence) {
        await this.#messageService.setUnreadMessage(newestMsg);
      }
      return;
    }

    // 2) subsequent runs => if the newest message sequence is bigger than last time
    if (highestSequence > lastReadSequence) {
      await this.#messageService.setUnreadMessage(newestMsg);
    }
  }

  async addUserAsParticipant() {
    const userId = this.user()?.id;
    if (!userId) throw new Error('User id is not defined');
    await this.#conversationHelperService.addAndUpdateParticipantToConversation(
      this.conversationId(),
      userId
    );
    setTimeout(() => {
      this.conversationComponent()?.conversationFooter()?.messageInput()?.focusInput();
    }, 0);
    await this.#messageService.setLastUnreadMessage(this.conversationId());
  }

  unlockConversation(event: string) {
    if (event) {
      this.#confirmUnlock(event);
    }
  }
  #confirmUnlock(id: string) {
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.TITLE'),
        message: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.MESSAGE'),
        confirmLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.LABEL'),
        cancelLabel: this.#translateService.instant('SHARED.PAGES.DASHBOARD.CONFIRM_UNLOCK.CANCEL'),
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#conversationHelperService.unlockConversation(id);
      }
    });
  }

  async menuClicked(value: { type: string; participant?: Signal<ParticipantWithSourceId> }) {
    if (value.participant) {
      if (value.type === 'edit') {
        this.#handleEditParticipant(value.participant());
      } else if (value.type === 'link') {
        await this.#handleParticipantConversationLink(value.participant());
      }
    }
  }

  #handleEditParticipant(participant: ParticipantWithSourceId) {
    const dialogRef = this.#dialog.open(EditParticipantDialogComponent, {
      data: {
        participant,
      },
      width: '25rem',
    });

    dialogRef.componentInstance?.submitClicked.subscribe(async (result) => {
      if (result) {
        if (participant?.id) {
          const response = await this.#activeConversationStore.editParticipant(
            this.conversationId(),
            participant.id,
            {
              displayName: result.displayName,
              phoneNumber: result.phoneNumber,
              email: result.email,
              preferredLanguage: result.preferredLanguage ?? undefined,
            }
          );
          dialogRef.componentInstance?.isSubmitting.set(false);
          if (response) {
            dialogRef.close();
            this.#toastr.success(
              this.#translateService.instant('SHARED.PAGES.DASHBOARD.EDIT_PARTICIPANT.SUCCESS')
            );
          } else {
            this.#toastr.error(
              this.#translateService.instant('SHARED.PAGES.DASHBOARD.EDIT_PARTICIPANT.ERROR')
            );
          }
        }
      }
    });
  }

  async #handleParticipantConversationLink(participant: ParticipantWithSourceId) {
    const url = await this.#activeConversationStore.getConversationLink(
      this.conversationId(),
      participant.id
    );
    if (url) {
      this.#clipboard.copy(url);
      this.#toastr.success(
        this.#translateService.instant('SHARED.PAGES.DASHBOARD.PARTICIPANT_LINK.SUCCESS'),
        undefined,
        {
          toastClass: 'ngx-toastr toastr-success-clipboard-copy',
        }
      );
    } else {
      this.#toastr.error(
        this.#translateService.instant('SHARED.PAGES.DASHBOARD.PARTICIPANT_LINK.ERROR')
      );
    }
  }
}
